/* global Swiper */
/**
 * Product Card Carousel - Shows image carousel on product card hover
 * Optimized with IntersectionObserver for lazy Swiper initialization
 * Dynamically updates carousel when product variant/color changes
 */
(function () {
  'use strict';

  if (typeof Swiper === 'undefined') {
    console.warn('Swiper library is not loaded. Product card carousel will not work.');
    return;
  }

  // Store initialized Swiper instances
  const productCardCarousels = new Map();

  // Store observed carousel elements
  const observedCarousels = new Set();

  // Store product handles for each carousel (for refresh logic)
  const carouselProductHandles = new Map();

  // Track if auto-slide hint has been shown
  let autoSlideHintShown = false;

  let isDesktop = window.matchMedia('(min-width: 1025px)').matches;

  // Update isDesktop on resize and handle viewport changes
  window.matchMedia('(min-width: 1025px)').addEventListener('change', (e) => {
    const wasDesktop = isDesktop;
    isDesktop = e.matches;
    smartCarouselState.isDesktop = e.matches;
    
    // Handle viewport change
    if (wasDesktop !== isDesktop) {
      handleViewportChange();
    }
  });

  /**
   * Get product media data from cached window object
   * Returns object with { media, hasVariants, optionNames } or just media array (legacy)
   */
  function getProductMediaData(productHandle) {
    // Check if media data is cached in window object
    if (window.productMediaData && window.productMediaData[productHandle]) {
      const data = window.productMediaData[productHandle];

      // Handle new structure: { media, hasVariants, optionNames }
      if (data.media) {
        return data;
      }

      // Handle legacy structure: just array of media
      if (Array.isArray(data)) {
        return { media: data, hasVariants: 1, optionNames: [] };
      }
    }

    // Fallback: check if product data exists in pdpData
    if (window.pdpData && Array.isArray(window.pdpData)) {
      const product = window.pdpData.find((p) => p.handle === productHandle);
      if (product && product.media) {
        return {
          media: product.media,
          hasVariants: product.variants?.length || 1,
          optionNames: product.options?.map((o) => o.name) || [],
        };
      }
    }

    console.warn(`No cached media data found for product: ${productHandle}`);
    return null;
  }

  /**
   * Filter media based on selected variant options (same logic as quick-drawer-product.liquid)
   * Media with alt text like "#Color_Blue" will be hidden if selected color is not Blue
   */
  function filterMediaForVariant(productMedia, selectedOptions, defaultMediaPosition) {
    if (!productMedia || !productMedia.length || !selectedOptions) {
      return productMedia;
    }

    return productMedia
      .map((media) => {
        // Keep non-image media or media without filtering metadata
        if (media.media_type !== 'image' || !media.alt || !media.alt.includes('#')) {
          return { ...media, filtered: false };
        }

        // Skip filtering for product title alt text
        if (media.alt === selectedOptions.productTitle) {
          return { ...media, filtered: false };
        }

        // Parse alt text: "Image #Color_Red" -> ["Image ", "Color_Red"]
        const altParts = media.alt.split('#');
        if (altParts.length < 2) {
          return { ...media, filtered: false };
        }

        // Parse group parts: "Color_Red" -> ["Color", "Red"]
        const mediaGroupParts = altParts[1].split('_');
        if (mediaGroupParts.length < 2) {
          return { ...media, filtered: false };
        }

        const optionName = mediaGroupParts[0].trim();
        const optionValue = mediaGroupParts[1].trim().toLowerCase();

        // Check if this option value matches the selected variant
        const selectedValue = selectedOptions[optionName];
        if (!selectedValue) {
          return { ...media, filtered: false };
        }

        // Filter out if values don't match and it's not the default media
        const shouldFilter =
          selectedValue.toLowerCase() !== optionValue && media.position !== defaultMediaPosition;

        return { ...media, filtered: shouldFilter };
      })
      .filter((media) => !media.filtered);
  }

  /**
   * Fallback: Fetch product data by handle via API (only if cached data not available)
   */
  async function fetchProductDataFallback(productHandle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      const productData = await response.json();

      // Cache it for future use with proper structure
      if (productData.media) {
        window.productMediaData = window.productMediaData || {};
        window.productMediaData[productHandle] = {
          media: productData.media,
          hasVariants: productData.variants?.length || 1,
          optionNames: productData.options?.map((o) => o.name) || [],
        };
      }

      return productData;
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  }

  /**
   * Get selected variant options from product card
   * Returns object like { Color: 'Red', Size: 'Medium' }
   */
  function getSelectedVariantOptions(productCard) {
    const selectedOptions = {};

    // Find all checked radio buttons for options
    const radioButtons = productCard.querySelectorAll(
      'input[type="radio"][name*="swatch"]:checked'
    );
    radioButtons.forEach((radio) => {
      const label = productCard.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        const value = radio.value;
        // Extract option name from the radio button's name or data attributes
        // Name format is usually like "swatch-section-id-product-id-position"
        const fieldset = radio.closest('fieldset');
        if (fieldset) {
          const optionPosition = fieldset.dataset.optionPosition;
          // We'll use the value directly since we match by value in alt text
          selectedOptions[`option${optionPosition}`] = value;
        }
      }
    });
    
    // If no checked radio buttons found, look for selected labels (swatches with is-selected class)
    if (Object.keys(selectedOptions).length === 0) {
      const selectedLabels = productCard.querySelectorAll(
        '.color-swatch.is-selected, .media-swatch.is-selected'
      );
      
      selectedLabels.forEach((label) => {
        const inputId = label.getAttribute('for');
        if (inputId) {
          const input = productCard.querySelector(`#${inputId}`);
          if (input) {
            const fieldset = input.closest('fieldset');
            if (fieldset) {
              const optionPosition = fieldset.dataset.optionPosition;
              const value = input.value;
              if (optionPosition && value) {
                selectedOptions[`option${optionPosition}`] = value;
              }
            }
          }
        }
      });
    }

    return selectedOptions;
  }

  /**
   * Build new slides HTML from product media
   * Optimized with eager loading and realistic srcset sizes for product cards
   * Media should already be filtered before passing to this function
   * Limited to maximum 10 images for performance
   */
  function buildCarouselSlides(productMedia, sizes) {
    if (!productMedia || !productMedia.length) return '';

    return productMedia
      .filter((media) => media.media_type === 'image')
      .slice(0, 10) // Limit to maximum 10 images
      .map((media) => {
        // Optimized srcset for product cards (200-1200px, realistic sizes)
        const widths = '200,300,400,500,600,800,1000,1200'.split(',');
        const srcset = widths.map((w) => `${media.preview_image.src}&width=${w} ${w}w`).join(', ');

        return `
                    <div class="swiper-slide">
                        <img
                            class="ri2s-img ri2s-img--cover product-card__carousel-image"
                            srcset="${srcset}"
                            sizes="${sizes || '100vw'}"
                            src="${media.preview_image.src}&width=600"
                            alt="${media.alt || ''}"
                            loading="lazy"
                            width="${media.preview_image.width}"
                            height="${media.preview_image.height}"
                        />
                    </div>
                `;
      })
      .join('');
  }

  /**
   * Preload images for a product handle
   * Starts downloading images in the background so they're cached when user clicks
   */
  function preloadProductImages(productHandle, sizes = '600') {
    const productDataObject = getProductMediaData(productHandle);
    if (!productDataObject) return;

    // Extract media array from data object
    const productMedia = productDataObject.media || productDataObject;
    if (!productMedia || !productMedia.length) return;

    // Only preload image media
    const images = productMedia.filter((media) => media.media_type === 'image');

    // Preload up to first 5 images (balance between UX and bandwidth)
    images.slice(0, 5).forEach((media, index) => {
      const img = new Image();

      // For first image, preload multiple sizes for srcset
      if (index === 0) {
        img.srcset = '200,300,400,500,600,800'
          .split(',')
          .map((w) => `${media.preview_image.src}&width=${w} ${w}w`)
          .join(', ');
        img.sizes = sizes || '100vw';
      }

      // Standard preload with optimal size
      img.src = `${media.preview_image.src}&width=600`;

      // Optional: add to a Map to prevent re-preloading
      // (Browser cache handles this naturally)
    });
  }

  /**
   * Update carousel with new product images
   * Uses cached media data from window.productMediaData (set by Liquid template)
   * Falls back to API fetch only if cached data is not available
   * Applies variant-based filtering (same logic as quick drawer)
   */
  async function updateCarousel(carouselElement, productHandle, sizes, productCard = null) {
    const swiper = productCardCarousels.get(carouselElement);
    if (!swiper || swiper.destroyed) return;

    // Get product card if not provided
    if (!productCard) {
      productCard = carouselElement.closest('product-card');
    }

    // Try to get cached media data first (fast, no API call)
    let productDataObject = getProductMediaData(productHandle);

    // Fallback: fetch from API if not cached (slower)
    if (!productDataObject) {
      const productData = await fetchProductDataFallback(productHandle);
      if (!productData || !productData.media) {
        console.error(`Failed to get media data for: ${productHandle}`);
        return;
      }
      productDataObject = {
        media: productData.media,
        hasVariants: productData.variants?.length || 1,
        optionNames: productData.options?.map((o) => o.name) || [],
      };
    }

    let productMedia = productDataObject.media;

    // Apply variant filtering if product has variants
    if (productDataObject.hasVariants > 1 && productCard) {
      const selectedOptions = getSelectedVariantOptions(productCard);

      // Get default media position (featured media)
      const primaryImage = productCard.querySelector('.product-card__image--primary');
      const defaultMediaPosition = null;

      // Create option map for filtering
      const optionMap = {};
      Object.keys(selectedOptions).forEach((key) => {
        // Try to match option name from optionNames array
        const position = parseInt(key.replace('option', ''));
        const optionName = productDataObject.optionNames[position - 1];
        if (optionName) {
          optionMap[optionName] = selectedOptions[key];
        }
      });

      // Filter media based on selected variant
      if (Object.keys(optionMap).length > 0) {
        productMedia = filterMediaForVariant(productMedia, optionMap, defaultMediaPosition);
      }
    }

    // Limit to maximum 10 images for performance
    productMedia = productMedia.slice(0, 10);

    // Build new slides with filtered media
    const newSlidesHTML = buildCarouselSlides(productMedia, sizes);
    if (!newSlidesHTML) {
      console.warn(`No slides generated for product: ${productHandle}`);
      return;
    }

    // Get the swiper wrapper
    const swiperWrapper = carouselElement.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return;

    // Update slides
    swiperWrapper.innerHTML = newSlidesHTML;

    // IMPORTANT: When loop mode is enabled, we need to destroy and reinit
    // because loop creates duplicate slides that need to be regenerated
    if (swiper.params.loop) {
      swiper.loopDestroy(); // Remove loop slides
      swiper.loopCreate(); // Recreate loop slides with new content
    }

    // Update Swiper (recalculate sizes, pagination, etc.)
    swiper.update();

    // Reinitialize pagination to sync with new slides
    if (swiper.pagination && swiper.pagination.render) {
      swiper.pagination.render();
      swiper.pagination.update();
    }

    // Go to first slide with no animation
    swiper.slideTo(0, 0);

    // Update stored handle
    carouselProductHandles.set(carouselElement, productHandle);
  }

  /**
   * Apply initial filtering to carousel slides for variant products
   * REMOVES filtered slides from DOM (not just hiding) so pagination counts correctly
   */
  function applyInitialFiltering(carouselElement, productCard, productHandle) {
    const productDataObject = getProductMediaData(productHandle);

    // Get all slide elements
    const slides = carouselElement.querySelectorAll('.swiper-slide');
    const productMedia = productDataObject?.media || [];

    // Track slides to keep
    let slidesKept = 0;

    // Get selected options for Type 2 filtering
    let optionMap = {};
    if (productDataObject && productDataObject.hasVariants > 1) {
      const selectedOptions = getSelectedVariantOptions(productCard);

      if (Object.keys(selectedOptions).length > 0) {
        Object.keys(selectedOptions).forEach((key) => {
          const position = parseInt(key.replace('option', ''));
          const optionName = productDataObject.optionNames[position - 1];
          if (optionName) {
            optionMap[optionName] = selectedOptions[key];
          }
        });
      }
    }

    // Filter and REMOVE slides (color filtering + 10-image limit)
    slides.forEach((slide, index) => {
      // Color filtering for Type 2 products (with #Color_ alt tags)
      if (productMedia[index] && Object.keys(optionMap).length > 0) {
        const media = productMedia[index];

        if (media.alt && media.alt.includes('#')) {
          const altParts = media.alt.split('#');
          if (altParts.length >= 2) {
            const mediaGroupParts = altParts[1].split('_');
            if (mediaGroupParts.length >= 2) {
              const optionName = mediaGroupParts[0].trim();
              const optionValue = mediaGroupParts[1].trim().toLowerCase();
              const selectedValue = optionMap[optionName];

              if (selectedValue && selectedValue.toLowerCase() !== optionValue && index !== 0) {
                // REMOVE this slide - doesn't match selected color
                slide.remove();
                return; // Skip to next slide
              }
            }
          }
        }
      }

      // Keep track of slides kept and enforce 10-image limit
      slidesKept++;
      if (slidesKept > 10) {
        slide.remove();
      }
    });
  }

  /**
   * Initialize Swiper carousel for a product card
   * Only called when the card enters the viewport (IntersectionObserver)
   *
   * Note: On mobile, carousel is always visible (CSS handles this)
   *       On desktop, carousel shows on hover (CSS handles this)
   *       JS only handles Swiper initialization for performance
   */
  function initializeCarousel(carouselElement, skipFiltering = false) {
    // Check if already initialized or in process
    if (!carouselElement || productCardCarousels.has(carouselElement)) {
      return;
    }

    // Mark as initialized in data attribute to prevent double init
    if (carouselElement.dataset.init === 'true') {
      return;
    }
    carouselElement.dataset.init = 'true';
    
    // Ensure element has proper structure
    if (!carouselElement.querySelector('.swiper-wrapper')) {
      console.error('Carousel element missing swiper-wrapper');
      return;
    }

    // Ensure element has aspect ratio CSS variable
    try {
      const aspectRatio = carouselElement.style.getPropertyValue('--product-image-aspect-ratio');
      if (!aspectRatio) {
        console.warn('Carousel element missing aspect ratio, setting default');
        carouselElement.style.setProperty('--product-image-aspect-ratio', '1');
      }
    } catch (e) {
      console.warn('Error checking aspect ratio, setting default:', e);
      carouselElement.style.setProperty('--product-image-aspect-ratio', '1');
    }

    const productCard = carouselElement.closest('product-card');
    if (!productCard) return;

    // Store the initial product handle
    const initialHandle = productCard.getAttribute('handle');
    if (initialHandle) {
      carouselProductHandles.set(carouselElement, initialHandle);
    }

    // Apply initial filtering BEFORE Swiper initialization
    // This removes filtered slides from DOM so pagination counts correctly
    // Skip if slides are already filtered (e.g., smart carousel)
    if (!skipFiltering) {
      applyInitialFiltering(carouselElement, productCard, initialHandle);
    }

    const config = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      speed: 300,
      lazy: {
        loadPrevNext: true,
        loadPrevNextAmount: 2,
      },
      // observer: true,
      observeParents: true,
      watchOverflow: true,
      // Touch/Swipe settings for smooth mobile experience
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      threshold: 10, // Minimum distance to trigger swipe (px)
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      shortSwipes: true,
      slidesPerGroup: 1, // Move one slide at a time
      //scrolling
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: false,
        sensitivity: 0.5, // Lower sensitivity for smoother control
      },
      // Navigation arrows (desktop only via CSS) - with safety checks
      navigation: (() => {
        const nextEl = carouselElement.querySelector('.product-card__carousel-button-next');
        const prevEl = carouselElement.querySelector('.product-card__carousel-button-prev');
        return (nextEl && prevEl) ? { nextEl, prevEl } : false;
      })(),
      // Pagination dots (mobile only via CSS) - with safety checks
      pagination: (() => {
        const paginationEl = carouselElement.querySelector('.product-card__carousel-pagination');
        return paginationEl ? {
          el: paginationEl,
          clickable: true,
          type: 'bullets',
          dynamicBullets: false,
        } : false;
      })(),
      on: {
        init: function () {
          carouselElement.classList.add('swiper-initialized');
        },
      },
    };

    try {
      // Ensure element is in DOM
      if (!carouselElement.isConnected) {
        console.error('Carousel element not connected to DOM');
        return;
      }
      
      const swiperInstance = new Swiper(carouselElement, config);
      productCardCarousels.set(carouselElement, swiperInstance);

      // Prevent carousel interaction from triggering product link
      carouselElement.addEventListener('click', handleCarouselClick);

      // Update swiper on hover for desktop only
      if (isDesktop) {
        // Listen on product card figure (image gallery) for hover
        const figure = productCard.querySelector('.product-card__figure');
        if (figure) {
          figure.addEventListener('mouseenter', handleMouseEnter);
        }
      }

      // Animation logic moved to buildSmartCarousel function
    } catch (error) {
      console.error('Error initializing product card carousel:', error);
      carouselElement.dataset.init = 'false';
    }
  }

  /**
   * Handle carousel click to prevent navigation
   */
  function handleCarouselClick(e) {
    if (
      e.target.closest('.swiper-button-next') ||
      e.target.closest('.swiper-button-prev') ||
      e.target.closest('.swiper-pagination')
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  /**
   * Handle mouse enter on product card figure (image gallery)
   */
  function handleMouseEnter(e) {
    const carousel = e.currentTarget.querySelector('[data-product-card-carousel]');
    if (carousel) {
      const swiper = productCardCarousels.get(carousel);
      if (swiper && !swiper.destroyed) {
        setTimeout(() => {
          if (!swiper.destroyed) {
            swiper.update();
          }
        }, 50);
      }
    }
  }

  /**
   * Destroy a carousel instance
   */
  function destroyCarousel(carouselElement) {
    const swiper = productCardCarousels.get(carouselElement);
    if (swiper && !swiper.destroyed) {
      swiper.destroy(true, true);
      productCardCarousels.delete(carouselElement);
      carouselProductHandles.delete(carouselElement);
      carouselElement.dataset.init = 'false';

      // Remove event listeners
      carouselElement.removeEventListener('click', handleCarouselClick);
      const productCard = carouselElement.closest('product-card');
      if (productCard) {
        // Remove listener from product card figure
        const figure = productCard.querySelector('.product-card__figure');
        if (figure) {
          figure.removeEventListener('mouseenter', handleMouseEnter);
        }
      }
    }
  }

  /**
   * Handle variant/color change on product card
   */
  function handleVariantChange(productCard, newProductHandle) {
    if (!productCard || !newProductHandle) return;

    const carouselContainer = productCard.querySelector('[data-product-card-carousel]');
    if (!carouselContainer) return;

    // Check if carousel is initialized
    const swiper = productCardCarousels.get(carouselContainer);
    if (!swiper || swiper.destroyed) return;

    // Get the current stored handle
    const currentHandle = carouselProductHandles.get(carouselContainer);

    // Only update if handle has changed
    if (currentHandle === newProductHandle) return;

    // Get sizes attribute from primary image for consistency
    const primaryImage = productCard.querySelector('.product-card__image--primary');
    const sizes = primaryImage ? primaryImage.getAttribute('sizes') : '100vw';

    // Update carousel with new product images (pass productCard for variant filtering)
    updateCarousel(carouselContainer, newProductHandle, sizes, productCard);
  }

  /**
   * Set up hover preloading on color swatches
   * When user hovers over a swatch, start preloading those images
   */
  function setupSwatchHoverPreloading() {
    // Debounce map to prevent multiple preloads for same product
    const preloadedHandles = new Set();

    // Listen for hover on color swatches (label.color-swatch)
    // Use mouseover instead of mouseenter for proper event delegation
    document.addEventListener(
      'mouseover',
      (event) => {
        // Check if event.target is a valid Element before calling closest
        if (!event.target || typeof event.target.closest !== 'function') return;

        const swatch = event.target.closest('label.color-swatch');
        if (!swatch) return;

        const productHandle = swatch.dataset.swatchProductHandle;
        if (!productHandle || preloadedHandles.has(productHandle)) return;

        preloadedHandles.add(productHandle);

        // Get sizes from product card if available
        const productCard = swatch.closest('product-card');
        const primaryImage = productCard?.querySelector('.product-card__image--primary');
        const sizes = primaryImage?.getAttribute('sizes') || '100vw';

        // Preload images in background
        preloadProductImages(productHandle, sizes);

      },
      true
    );

    // Also listen for hover on swatch-link (image-based swatches)
    document.addEventListener(
      'mouseover',
      (event) => {
        // Check if event.target is a valid Element before calling closest
        if (!event.target || typeof event.target.closest !== 'function') return;

        const swatchLink = event.target.closest('a.swatch-link');
        if (!swatchLink) return;

        // Extract handle from href (e.g., /products/product-handle)
        const href = swatchLink.getAttribute('href');
        if (!href) return;

        const match = href.match(/\/products\/([^?#]+)/);
        if (!match) return;

        const productHandle = match[1];
        if (preloadedHandles.has(productHandle)) return;

        // Mark as preloaded
        preloadedHandles.add(productHandle);

        // Preload images
        preloadProductImages(productHandle);

      },
      true
    ); // Use capture phase
  }

  /**
   * Listen for swatch changes from custom.js and theme.js
   */
  function setupSwatchListeners() {
    // Listen for custom event from custom.js (group products)
    document.addEventListener('product-card:variant-changed', (event) => {
      const { productCard, productHandle } = event.detail || {};
      if (productCard && productHandle) {
        handleVariantChange(productCard, productHandle);
      }
    });

    // Listen for native radio button changes from theme.js (standard variant swatches)
    // This handles 'swatch' and 'variant' display modes
    document.addEventListener('change', (event) => {
      if (
        event.target.type === 'radio' &&
        event.target.name.includes('swatch') &&
        event.target.closest('product-card')
      ) {
        const productCard = event.target.closest('product-card');
        const productHandle = productCard.getAttribute('handle');
        if (productHandle) {
          // For standard variants, the handle stays the same
          // But we need to rebuild carousel with filtered media for the new variant
          // Small delay to let theme.js update images first
          setTimeout(() => {
            const carouselContainer = productCard.querySelector('[data-product-card-carousel]');
            const swiper = productCardCarousels.get(carouselContainer);

            if (swiper && !swiper.destroyed) {
              // Get sizes from primary image
              const primaryImage = productCard.querySelector('.product-card__image--primary');
              const sizes = primaryImage ? primaryImage.getAttribute('sizes') : '100vw';

              // Rebuild carousel with filtered media for new variant
              // This will apply media filtering based on newly selected options
              updateCarousel(carouselContainer, productHandle, sizes, productCard);
            }
          }, 150);
        }
      }
    });
  }

  /**
   * IntersectionObserver callback
   * Initializes Swiper when carousel enters viewport
   */
  function handleIntersection(entries, observer) {
    entries.forEach((entry) => {
      const carouselElement = entry.target;

      if (entry.isIntersecting) {
        // Element is in viewport, initialize Swiper
        if (!productCardCarousels.has(carouselElement)) {
          initializeCarousel(carouselElement);
        }
        // Stop observing once initialized
        observer.unobserve(carouselElement);
        observedCarousels.delete(carouselElement);
      }
    });
  }

  /**
   * Create IntersectionObserver with appropriate options
   */
  const carouselObserver = new IntersectionObserver(handleIntersection, {
    root: null, // viewport
    rootMargin: '50px', // Start loading 50px before entering viewport
    threshold: 0.01, // Trigger as soon as 1% is visible
  });

  /**
   * Observe all product card carousels for lazy initialization
   * Updated to work with smart carousel builder
   */
  function observeCarousels() {
    const carousels = document.querySelectorAll('[data-product-card-carousel][data-init="false"]');

    carousels.forEach((carousel) => {
      // Don't observe if already being observed or initialized
      if (!observedCarousels.has(carousel) && !productCardCarousels.has(carousel)) {
        carouselObserver.observe(carousel);
        observedCarousels.add(carousel);
      }
    });
  }

  function checkSmartCarouselBuilder() {
    if (window.smartCarouselBuilder) {
      return true;
    }
    return false;
  }

  const smartCarouselState = {
    initialized: new Set(),
    building: new Set(),
    hoverTimeouts: new Map(),
    isDesktop: window.matchMedia('(min-width: 1025px)').matches,
    isInitialPageLoad: true,
    isReinitializing: false
  };

  // Update desktop detection on resize
  window.matchMedia('(min-width: 1025px)').addEventListener('change', (e) => {
    smartCarouselState.isDesktop = e.matches;
  });

  /**
   * Extract media data from product card DOM
   */
  function extractMediaDataFromProductCard(productCard) {
    try {
      // Look for product images in the DOM
      const productImages = productCard.querySelectorAll('img[data-product-image], .product-card__image img, [data-media-id]');
      const mediaData = [];
      
      productImages.forEach((img, index) => {
        const src = img.src || img.dataset.src;
        const alt = img.alt || '';
        const mediaId = img.dataset.mediaId || `media-${index}`;
        
        if (src) {
          mediaData.push({
            id: mediaId,
            media_type: 'image',
            src: src,
            alt: alt,
            width: img.naturalWidth || 800,
            height: img.naturalHeight || 800
          });
        }
      });
      
      return mediaData.length > 0 ? { media: mediaData } : null;
    } catch (error) {
      console.error('Error extracting media data from product card:', error);
      return null;
    }
  }

  /**
   * Load product media data dynamically
   */
  async function loadProductMediaData(productHandle) {
    try {
      // Try to fetch product data from Shopify's product API
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch product data: ${response.status}`);
      }
      
      const productData = await response.json();
      
      // Transform the data to match the expected format
      const mediaData = {
        media: productData.media || [],
        variants: productData.variants || []
      };
      
      // Store in window.productMediaData
      if (!window.productMediaData) {
        window.productMediaData = {};
      }
      window.productMediaData[productHandle] = mediaData;
      
      return mediaData;
    } catch (error) {
      console.error('Error loading product media data:', error);
      return null;
    }
  }

  /**
   * Build carousel HTML from media data
   */
  function buildCarouselHTMLFromMedia(media, productCard) {
    if (!media || media.length === 0) return '';
    
    // Get aspect ratio from container
    const container = productCard.querySelector('[data-carousel-lazy="true"]');
    let aspectRatio = '1';
    
    if (container && container.style) {
      try {
        aspectRatio = container.style.getPropertyValue('--product-image-aspect-ratio') || '1';
      } catch (e) {
        console.warn('Error getting aspect ratio from container style:', e);
        aspectRatio = '1';
      }
    }
    
    // Build carousel HTML
    const slidesHTML = media.map((mediaItem, index) => {
      const isFirst = index === 0;
      const isLast = index === media.length - 1;
      
      return `
        <div class="swiper-slide" data-media-id="${mediaItem.id}" data-media-index="${index}">
          <div class="product-card__image" style="aspect-ratio: ${aspectRatio};">
            <img
              src="${mediaItem.src}"
              alt="${mediaItem.alt || ''}"
              loading="${isFirst ? 'eager' : 'lazy'}"
              data-media-id="${mediaItem.id}"
              data-media-index="${index}"
              style="width: 100%; height: 100%; object-fit: cover;"
            />
          </div>
        </div>
      `;
    }).join('');
    
    return `
      <div class="product-card__carousel swiper" data-product-card-carousel data-init="false" style="--product-image-aspect-ratio: ${aspectRatio};">
        <div class="swiper-wrapper">
          ${slidesHTML}
        </div>
        <div class="product-card__carousel-button-prev swiper-button-prev"></div>
        <div class="product-card__carousel-button-next swiper-button-next"></div>
        <div class="product-card__carousel-pagination swiper-pagination"></div>
      </div>
    `;
  }

  /**
   * Build carousel HTML for smart system
   */
  function buildSmartCarouselHTML(productCard, productHandle) {
    const productData = window.productMediaData?.[productHandle];
    if (!productData?.media) {
      // Try to extract media data from the product card DOM
      const extractedData = extractMediaDataFromProductCard(productCard);
      
      if (extractedData) {
        // Store the extracted data in window.productMediaData for consistency
        if (!window.productMediaData) {
          window.productMediaData = {};
        }
        window.productMediaData[productHandle] = extractedData;
        
        // Continue with the extracted data
        const media = extractedData.media.filter(m => m.media_type === 'image').slice(0, 10);
        if (media.length > 0) {
          return buildCarouselHTMLFromMedia(media, productCard);
        } else {
          // Fall through to return loading fallback
        }
      }
      
      if (!extractedData || !extractedData.media || extractedData.media.length === 0) {
        // Try to load media data dynamically from API
        loadProductMediaData(productHandle).then((mediaData) => {
          if (mediaData) {
            // Rebuild the carousel with the loaded data
            const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
            if (carouselContainer) {
              const newCarouselHTML = buildSmartCarouselHTML(productCard, productHandle);
              if (newCarouselHTML) {
                carouselContainer.innerHTML = newCarouselHTML;
                // Initialize the carousel
                const newCarouselElement = carouselContainer.querySelector('[data-product-card-carousel]');
                if (newCarouselElement) {
                  // Skip filtering since slides are already filtered in buildSmartCarouselHTML
                  initializeCarousel(newCarouselElement, true);
                  smartCarouselState.initialized.add(carouselContainer);
                }
              }
            }
          }
        }).catch((error) => {
          console.error('Failed to load media data:', error);
        });
      }
      
      // Get aspect ratio from container with safe access
      const container = productCard.querySelector('[data-carousel-lazy="true"]');
      let aspectRatio = '1'; // Default fallback
      
      if (container) {
        if (container.style) {
          try {
            aspectRatio = container.style.getPropertyValue('--product-image-aspect-ratio') || '1';
          } catch (e) {
            console.warn('Error getting aspect ratio from container style:', e);
            aspectRatio = '1';
          }
        } else {
          console.warn('Container has no style property, using default aspect ratio');
          aspectRatio = '1';
        }
      } else {
        console.warn('No container found, using default aspect ratio');
        aspectRatio = '1';
      }
      
      // Return a fallback carousel
      const fallbackHTML = `
        <div class="product-card__carousel swiper" data-product-card-carousel data-init="false" style="--product-image-aspect-ratio: ${aspectRatio};">
          <div class="swiper-wrapper">
            <div class="swiper-slide">
              <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666; aspect-ratio: ${aspectRatio};">
                <span>Loading images...</span>
              </div>
            </div>
          </div>
          <div class="product-card__carousel-button-prev swiper-button-prev"></div>
          <div class="product-card__carousel-button-next swiper-button-next"></div>
          <div class="product-card__carousel-pagination swiper-pagination"></div>
        </div>
      `;
      
      return fallbackHTML;
    }

    let media = productData.media.filter(m => m.media_type === 'image');
    
    // Apply variant filtering if product has variants (like color as variant)
    if (productData.hasVariants > 1 && productCard) {
      const selectedOptions = getSelectedVariantOptions(productCard);
      
      // Create option map for filtering
      const optionMap = {};
      Object.keys(selectedOptions).forEach((key) => {
        // Try to match option name from optionNames array
        const position = parseInt(key.replace('option', ''));
        const optionName = productData.optionNames[position - 1];
        if (optionName) {
          optionMap[optionName] = selectedOptions[key];
        }
      });
      
      // Filter media based on selected variant (e.g., color)
      if (Object.keys(optionMap).length > 0) {
        media = filterMediaForVariant(media, optionMap, null);
      } else {
        // If no options detected, look for selected swatches in the DOM
        // Check for is-selected class or checked radio buttons
        const selectedSwatches = productCard.querySelectorAll(
          '.color-swatch.is-selected, .media-swatch.is-selected, input[type="radio"]:checked'
        );
        
        if (selectedSwatches.length > 0) {
          selectedSwatches.forEach((element) => {
            // For labels with is-selected class
            if (element.classList.contains('is-selected')) {
              // Find the related input
              const inputId = element.getAttribute('for');
              if (inputId) {
                const input = productCard.querySelector(`#${inputId}`);
                if (input) {
                  const fieldset = input.closest('fieldset');
                  const value = input.value;
                  const optionPosition = fieldset?.dataset.optionPosition;
                  if (optionPosition && value) {
                    optionMap[`option${optionPosition}`] = value;
                  }
                }
              }
            } else if (element.checked && element.type === 'radio') {
              // Direct radio button
              const fieldset = element.closest('fieldset');
              const value = element.value;
              const optionPosition = fieldset?.dataset.optionPosition;
              if (optionPosition && value) {
                optionMap[`option${optionPosition}`] = value;
              }
            }
          });
          
          if (Object.keys(optionMap).length > 0) {
            media = filterMediaForVariant(media, optionMap, null);
          }
        }
      }
    }

    // Limit to maximum 10 images for performance
    media = media.slice(0, 10);
    
    if (media.length === 0) {
      console.warn(`No image media found for product: ${productHandle}`);
      return '';
    }

    // Get sizes from primary image
    const primaryImage = productCard.querySelector('.product-card__image--primary');
    const sizes = primaryImage?.getAttribute('sizes') || '100vw';

    // Build slides HTML
    const slidesHTML = media.map(mediaItem => {
      const srcset = '200,300,400,500,600,800,1000,1200'
        .split(',')
        .map(w => `${mediaItem.preview_image.src}&width=${w} ${w}w`)
        .join(', ');

      return `
        <div class="swiper-slide">
          <img
            class="ri2s-img ri2s-img--cover product-card__carousel-image"
            srcset="${srcset}"
            sizes="${sizes}"
            src="${mediaItem.preview_image.src}&width=600"
            alt="${mediaItem.alt || ''}"
            loading="lazy"
            width="${mediaItem.preview_image.width}"
            height="${mediaItem.preview_image.height}"
          />
        </div>
      `;
    }).join('');

    // Get aspect ratio from container with safe access
    const container = productCard.querySelector('[data-carousel-lazy="true"]');
    let aspectRatio = '1'; // Default fallback
    
    if (container) {
      if (container.style) {
        try {
          aspectRatio = container.style.getPropertyValue('--product-image-aspect-ratio') || '1';
        } catch (e) {
          console.warn('Error getting aspect ratio from container style:', e);
          aspectRatio = '1';
        }
      } else {
        console.warn('Container has no style property, using default aspect ratio');
        aspectRatio = '1';
      }
    } else {
      console.warn('No container found, using default aspect ratio');
      aspectRatio = '1';
    }
    
    const carouselHTML = `
      <div class="product-card__carousel swiper" data-product-card-carousel data-init="false" style="--product-image-aspect-ratio: ${aspectRatio};">
        <div class="swiper-wrapper">
          ${slidesHTML}
        </div>
        <div class="product-card__carousel-button-prev swiper-button-prev"></div>
        <div class="product-card__carousel-button-next swiper-button-next"></div>
        <div class="product-card__carousel-pagination swiper-pagination"></div>
      </div>
    `;
    
    return carouselHTML;
  }

  /**
   * Trigger slide animation for first product on mobile
   */
  function triggerMobileSlideAnimation(productCard, carouselContainer) {
    if (smartCarouselState.isDesktop || autoSlideHintShown) return;
    
    // Mark as triggered immediately to prevent duplicate calls
    autoSlideHintShown = true;
    
    setTimeout(() => {
      const carouselEl = carouselContainer.querySelector('[data-product-card-carousel]');
      if (!carouselEl) {
        return;
      }
      
      const swiper = productCardCarousels.get(carouselEl);
      if (!swiper || swiper.destroyed || !swiper.slides || swiper.slides.length < 2) {
        return;
      }
      
      // Ensure we're at slide 0 before animating
      swiper.slideTo(0, 0, false);
      
      // Small delay to ensure slide position is settled
      setTimeout(() => {
        if (!swiper.destroyed && swiper.slides && swiper.slides.length > 1) {
          // Slide to next
          swiper.translateTo(-45, 500, false, true);
          
          // Slide back to first after 0.7 seconds
          setTimeout(() => {
            if (!swiper.destroyed) {
              swiper.translateTo(0, 500, false, true);
            }
          }, 700);
        }
      }, 100);
    }, 2500); // Wait longer for carousel to be fully initialized
  }

  /**
   * Build smart carousel
   */
  function buildSmartCarousel(productCard) {    
    const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
    if (!carouselContainer) {
      console.warn('No lazy carousel container found');
      return;
    }
    
    if (smartCarouselState.building.has(carouselContainer)) {
      return;
    }

    const productHandle = productCard.getAttribute('handle');
    if (!productHandle) {
      console.warn('No product handle found');
      return;
    }

    // Ensure container has aspect ratio set with comprehensive safety check
    try {
      if (carouselContainer.style) {
        const currentAspectRatio = carouselContainer.style.getPropertyValue('--product-image-aspect-ratio');
        if (!currentAspectRatio) {
          console.warn('Container missing aspect ratio, setting default');
          carouselContainer.style.setProperty('--product-image-aspect-ratio', '1');
        }
      } else {
        console.warn('Container has no style property, setting aspect ratio via setAttribute');
        carouselContainer.setAttribute('style', '--product-image-aspect-ratio: 1;');
      }
    } catch (e) {
      console.error('Error setting aspect ratio:', e);
      // Fallback: set via setAttribute
      carouselContainer.setAttribute('style', '--product-image-aspect-ratio: 1;');
    }

    smartCarouselState.building.add(carouselContainer);

    // Build carousel HTML
    const carouselHTML = buildSmartCarouselHTML(productCard, productHandle);
    
    if (!carouselHTML) {
      console.warn('Failed to build carousel HTML for product:', productHandle);
      smartCarouselState.building.delete(carouselContainer);
      return;
    }

    // Replace placeholder with actual carousel
    const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
    if (placeholder) {
      // Clear the container first to avoid duplicates
      carouselContainer.innerHTML = '';
      
      // Insert the new carousel HTML directly into the container
      carouselContainer.insertAdjacentHTML('beforeend', carouselHTML);
    } else {
      console.warn('No placeholder found, inserting carousel HTML directly');
      carouselContainer.innerHTML = carouselHTML;
    }

    // Initialize Swiper with a small delay to ensure DOM is updated
    setTimeout(() => {
      const newCarouselElement = carouselContainer.querySelector('[data-product-card-carousel]');
      
      if (newCarouselElement && newCarouselElement.querySelector('.swiper-wrapper')) {
        // Skip filtering since slides are already filtered in buildSmartCarouselHTML
        initializeCarousel(newCarouselElement, true);
        smartCarouselState.initialized.add(carouselContainer);
        
        // Note: Animation is handled separately for better timing
      } else {
        console.error('Carousel element missing or incomplete after HTML replacement');
      }
    }, 10);

    smartCarouselState.building.delete(carouselContainer);
  }

  /**
   * Check if product is in viewport for mobile scroll detection
   */
  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const threshold = windowHeight * 0.1; // 10% threshold
    
    return (
      rect.top < windowHeight - threshold &&
      rect.bottom > threshold
    );
  }

  /**
   * Handle viewport changes (mobile ↔ desktop)
   */
  function handleViewportChange() {    
    // Clear existing timeouts
    smartCarouselState.hoverTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    smartCarouselState.hoverTimeouts.clear();
    
    // For desktop: ensure all products have hover listeners
    if (isDesktop) {
      const productCards = document.querySelectorAll('product-card');
      productCards.forEach((productCard) => {
        const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
        if (!carouselContainer) return;
        
        // Remove existing listeners by cloning the element
        const newProductCard = productCard.cloneNode(true);
        productCard.parentNode.replaceChild(newProductCard, productCard);
        
        // Set up hover listeners for desktop
        newProductCard.addEventListener('mouseenter', () => {
          const newCarouselContainer = newProductCard.querySelector('[data-carousel-lazy="true"]');
          if (!newCarouselContainer) return;
          
          // Check if carousel is already built
          if (smartCarouselState.initialized.has(newCarouselContainer)) {
            return;
          }
          
          // Show spinner on hover
          const placeholder = newCarouselContainer.querySelector('[data-carousel-placeholder]');
          if (placeholder) {
            placeholder.style.display = 'flex';
          }
          
          const timeoutId = setTimeout(() => {
            buildSmartCarousel(newProductCard);
          }, 150);
          smartCarouselState.hoverTimeouts.set(newCarouselContainer, timeoutId);
        });
        
        newProductCard.addEventListener('mouseleave', () => {
          const newCarouselContainer = newProductCard.querySelector('[data-carousel-lazy="true"]');
          if (!newCarouselContainer) return;
          
          // Hide spinner on mouse leave
          const placeholder = newCarouselContainer.querySelector('[data-carousel-placeholder]');
          if (placeholder && !smartCarouselState.initialized.has(newCarouselContainer)) {
            placeholder.style.display = 'none';
          }
          
          const timeoutId = smartCarouselState.hoverTimeouts.get(newCarouselContainer);
          if (timeoutId) {
            clearTimeout(timeoutId);
            smartCarouselState.hoverTimeouts.delete(newCarouselContainer);
          }
        });
      });
    }
  }

  /**
   * Set up intersection observer for desktop to preload images and build carousel HTML
   */
  function setupDesktopPreloadObserver() {
    const desktopObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const productCard = entry.target;
          const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
          
          if (carouselContainer && !smartCarouselState.initialized.has(carouselContainer)) {
            // Check if carousel HTML is already built
            const existingCarousel = carouselContainer.querySelector('[data-product-card-carousel]');
            if (!existingCarousel) {
              // Build carousel HTML but don't initialize Swiper yet
              buildCarouselHTMLOnly(productCard);
            }
          }
        }
      });
    }, {
      root: null,
      rootMargin: '200px', // Start preloading 200px before entering viewport
      threshold: 0.1
    });

    // Observe all product cards for desktop preloading
    const productCards = document.querySelectorAll('product-card');
    productCards.forEach((productCard) => {
      const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
      if (carouselContainer) {
        desktopObserver.observe(productCard);
      }
    });
  }

  /**
   * Build carousel HTML only (without initializing Swiper)
   */
  function buildCarouselHTMLOnly(productCard) {
    const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
    if (!carouselContainer) return;

    const productHandle = productCard.getAttribute('handle');
    if (!productHandle) return;

    // Build carousel HTML
    const carouselHTML = buildSmartCarouselHTML(productCard, productHandle);
    if (!carouselHTML) return;

    // Insert carousel HTML into DOM
    const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
    if (placeholder) {
      carouselContainer.innerHTML = '';
      carouselContainer.insertAdjacentHTML('beforeend', carouselHTML);
    } else {
      carouselContainer.innerHTML = carouselHTML;
    }

    console.log('Carousel HTML built for desktop preload:', productHandle);
  }

  /**
   * Handle scroll events for mobile carousel building
   */
  function handleSmartScroll() {
    if (smartCarouselState.isDesktop) return;

    // Only check products that haven't been initialized yet
    const productCards = document.querySelectorAll('product-card');
    
    productCards.forEach((productCard, index) => {
      // Skip first 2 products (already loaded immediately)
      if (index < 2) return;
      
      const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
      
      // Only process if container exists and not already initialized
      if (carouselContainer && !smartCarouselState.initialized.has(carouselContainer)) {
        if (isInViewport(productCard)) {
          buildSmartCarousel(productCard);
        }
      }
    });
  }

  /**
   * Initialize hover listeners for a single product card
   */
  function initializeProductCardHoverListeners(productCard) {
    const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
    if (!carouselContainer) return;
    
    // Skip if already initialized
    if (smartCarouselState.initialized.has(carouselContainer)) {
      return;
    }
    
    // Desktop: Set up hover listeners
    if (smartCarouselState.isDesktop) {
      productCard.addEventListener('mouseenter', () => {
        // Check if carousel is already built
        if (smartCarouselState.initialized.has(carouselContainer)) {
          return;
        }
        
        // Show spinner on hover
        const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
        if (placeholder) {
          placeholder.style.display = 'flex';
        }
        
        const timeoutId = setTimeout(() => {
          buildSmartCarousel(productCard);
        }, 150);
        smartCarouselState.hoverTimeouts.set(carouselContainer, timeoutId);
      });
      
      productCard.addEventListener('mouseleave', () => {
        // Hide spinner on mouse leave
        const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
        if (placeholder && !smartCarouselState.initialized.has(carouselContainer)) {
          placeholder.style.display = 'none';
        }
        
        const timeoutId = smartCarouselState.hoverTimeouts.get(carouselContainer);
        if (timeoutId) {
          clearTimeout(timeoutId);
          smartCarouselState.hoverTimeouts.delete(carouselContainer);
        }
      });
    }
    // Mobile: Use IntersectionObserver for lazy loading
    else {
      if (!productCardCarousels.has(carouselContainer)) {
        carouselObserver.observe(carouselContainer);
        observedCarousels.add(carouselContainer);
      }
    }
  }

  /**
   * Initialize smart carousel system
   */
  function initializeSmartCarouselSystem() {
    
    const productCards = document.querySelectorAll('product-card');
    
    
    productCards.forEach((productCard, index) => {
      const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
      
      
      // Desktop: All products hover-triggered
      if (smartCarouselState.isDesktop) {
        productCard.addEventListener('mouseenter', () => {
          // Check if carousel is already built
          if (smartCarouselState.initialized.has(carouselContainer)) {
            return;
          }
          
          // Show spinner on hover
          const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
          if (placeholder) {
            placeholder.style.display = 'flex';
          }
          
          const timeoutId = setTimeout(() => {
            buildSmartCarousel(productCard);
          }, 150);
          smartCarouselState.hoverTimeouts.set(carouselContainer, timeoutId);
        });
        
        productCard.addEventListener('mouseleave', () => {
          // Hide spinner on mouse leave
          const placeholder = carouselContainer.querySelector('[data-carousel-placeholder]');
          if (placeholder && !smartCarouselState.initialized.has(carouselContainer)) {
            placeholder.style.display = 'none';
          }
          
          const timeoutId = smartCarouselState.hoverTimeouts.get(carouselContainer);
          if (timeoutId) {
            clearTimeout(timeoutId);
            smartCarouselState.hoverTimeouts.delete(carouselContainer);
          }
        });
      }
      
      // Mobile: First 2 products immediate, rest on observer
      // For pagination: use IntersectionObserver instead of immediate load
      else {
        // Only load first 2 on initial page load, not after pagination
        if (index < 2 && smartCarouselState.isInitialPageLoad) {
          setTimeout(() => {
            buildSmartCarousel(productCard);
          }, 100);
          
          // Trigger animation for first product only
          if (index === 0 && !autoSlideHintShown) {
            triggerMobileSlideAnimation(productCard, carouselContainer);
          }
        } else {
          if (!productCardCarousels.has(carouselContainer)) {
            carouselObserver.observe(carouselContainer);
            observedCarousels.add(carouselContainer);
          }
        }
      }
    });

    // Set up scroll listener for mobile - load while scrolling, not on scroll stop
    window.addEventListener('scroll', () => {
      handleSmartScroll();
    }, { passive: true });

    // Set up intersection observer for desktop to preload and build carousels
    if (smartCarouselState.isDesktop) {
      setupDesktopPreloadObserver();
    }

    // Mark initial load as complete after a delay
    setTimeout(() => {
      smartCarouselState.isInitialPageLoad = false;
    }, 3000);
  }

  /**
   * Clean up carousels and observers that no longer exist in the DOM
   */
  function cleanupRemovedCarousels() {
    // Clean up initialized carousels
    productCardCarousels.forEach((swiper, element) => {
      if (!document.body.contains(element)) {
        destroyCarousel(element);
      }
    });

    // Clean up observed elements
    observedCarousels.forEach((element) => {
      if (!document.body.contains(element)) {
        carouselObserver.unobserve(element);
        observedCarousels.delete(element);
      }
    });
  }

  /**
   * Re-observe carousels (for dynamic content)
   */
  function refreshObserver() {
    observeCarousels();
    cleanupRemovedCarousels();
  }

  // Initialize observation on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Check if smart carousel builder is available
      if (!checkSmartCarouselBuilder()) {
        observeCarousels();
      }
      setupSwatchListeners();
      setupSwatchHoverPreloading();
      
      // Initialize smart carousel system
      initializeSmartCarouselSystem();
      
      // Also retry initialization after a delay to catch dynamically loaded content
      setTimeout(() => {
        const productCards = document.querySelectorAll('product-card');
        if (productCards.length > 0) {
          initializeSmartCarouselSystem();
        }
      }, 2000);
    });
  } else {
    // Check if smart carousel builder is available
    if (!checkSmartCarouselBuilder()) {
      observeCarousels();
    }
    setupSwatchListeners();
    setupSwatchHoverPreloading();
    
    // Initialize smart carousel system
    initializeSmartCarouselSystem();
    
    // Also retry initialization after a delay to catch dynamically loaded content
    setTimeout(() => {
      const productCards = document.querySelectorAll('product-card');
      if (productCards.length > 0) {        initializeSmartCarouselSystem();
      }
    }, 2000);
  }

  // Re-observe when new content is loaded (e.g., infinite scroll, filters)
  document.addEventListener('theme:product-card:refresh', () => {
    refreshObserver();
  });

  // Listen for facet/collection updates
  document.addEventListener('facet:update', () => {
    // Prevent duplicate reinitialization
    if (smartCarouselState.isReinitializing) return;
    smartCarouselState.isReinitializing = true;
    
    // Clean up all existing carousel instances before reinitializing
    productCardCarousels.forEach((swiper, element) => {
      if (!swiper.destroyed) {
        swiper.destroy(true, true);
      }
    });
    productCardCarousels.clear();
    observedCarousels.clear();
    carouselProductHandles.clear();
    smartCarouselState.initialized.clear();
    smartCarouselState.building.clear();
    smartCarouselState.hoverTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    smartCarouselState.hoverTimeouts.clear();
    
    // Reset initial page load flag since filters create new content
    smartCarouselState.isInitialPageLoad = false;
    
    // Reset animation flag so first product in new content can animate
    autoSlideHintShown = false;
    
    // Reinitialize for all product cards after a delay to let DOM update
    setTimeout(() => {
      const productCards = document.querySelectorAll('product-card');
      
      // For mobile: load first 2 products immediately first, before setting up listeners
      if (!smartCarouselState.isDesktop) {
        productCards.forEach((productCard, index) => {
          if (index < 2) {
            const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
            if (carouselContainer) {
              buildSmartCarousel(productCard);
            }
          } else {
            // For products beyond first 2, set up observer/listeners
            initializeProductCardHoverListeners(productCard);
          }
        });
        
        // Trigger animation for first product after a delay to ensure carousel is ready
        if (!autoSlideHintShown && productCards.length > 0) {
          setTimeout(() => {
            const firstProductCard = productCards[0];
            const carouselContainer = firstProductCard.querySelector('[data-carousel-lazy="true"]');
            if (carouselContainer) {
              triggerMobileSlideAnimation(firstProductCard, carouselContainer);
            }
          }, 100);
        }
      } else {
        // Desktop: set up all hover listeners
        productCards.forEach((productCard) => {
          initializeProductCardHoverListeners(productCard);
        });
      }
      
      smartCarouselState.isReinitializing = false;
    }, 300);
  });

  // MutationObserver for dynamically added product cards
  const mutationObserver = new MutationObserver((mutations) => {
    // Skip if facet update is already handling reinitialization
    if (smartCarouselState.isReinitializing) return;
    
    let shouldRefresh = false;
    let newProductCards = [];

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check if it's a product card or contains one
          if (node.matches && node.matches('product-card')) {
            newProductCards.push(node);
            shouldRefresh = true;
          } else if (node.querySelector && node.querySelector('product-card')) {
            const cards = node.querySelectorAll('product-card');
            newProductCards.push(...Array.from(cards));
            shouldRefresh = true;
          }
        }
      });
    });

    if (shouldRefresh) {
      
      // Reinitialize smart carousel system for new products only
      if (newProductCards.length > 0) {
        // For mobile: immediately load first 2 products that are added
        if (!smartCarouselState.isDesktop) {
          newProductCards.slice(0, 2).forEach((productCard) => {
            const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
            if (carouselContainer) {
              // Immediately build carousel for first 2 products on mobile
              buildSmartCarousel(productCard);
            }
          });
          // For remaining products, use IntersectionObserver
          if (newProductCards.length > 2) {
            newProductCards.slice(2).forEach((productCard) => {
              initializeProductCardHoverListeners(productCard);
            });
          }
        } else {
          // Desktop: normal hover listener initialization
          newProductCards.forEach((productCard) => {
            initializeProductCardHoverListeners(productCard);
          });
        }
      }
      // Debounce the refresh
      setTimeout(refreshObserver, 100);
    }
  });

  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Expose API for external use
  window.productCardCarousel = {
    observe: observeCarousels,
    refresh: refreshObserver,
    destroy: destroyCarousel,
    cleanup: cleanupRemovedCarousels,
    updateCarousel: updateCarousel, // Expose for external trigger
    preloadImages: preloadProductImages, // Expose for manual preloading
    // For debugging
    get stats() {
      return {
        initialized: productCardCarousels.size,
        observed: observedCarousels.size,
        cachedMediaProducts: window.productMediaData
          ? Object.keys(window.productMediaData).length
          : 0,
      };
    },
    // Debug helpers
    getCachedMedia(handle) {
      return getProductMediaData(handle);
    },
    // Debug final optimization system
    debugTiers() {
      const productCards = document.querySelectorAll('product-card');
      productCards.forEach((productCard, index) => {
        const carouselContainer = productCard.querySelector('[data-carousel-lazy="true"]');
        
        let behavior;
        if (smartCarouselState.isDesktop) {
          behavior = 'Hover-triggered';
        } else {
          behavior = index < 2 ? 'Immediate' : 'Observer';
        }
      });
    }
  };
})();
